import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useConsignorId } from '../hooks/useConsignorId';
import { SettlementReport } from '../types/consignment.types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) => `$${Math.abs(n).toFixed(2)}`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const classes: Record<string, string> = {
    paid:   'bg-green-100 text-green-800',
    unpaid: 'bg-red-100 text-red-700',
    check:  'bg-blue-100 text-blue-800',
    ach:    'bg-blue-100 text-blue-800',
    wire:   'bg-purple-100 text-purple-800',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${classes[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const ConsignorSettlementView: React.FC<{ consignorIdOverride?: string; basePath?: string }> = ({
  consignorIdOverride,
  basePath = '/consignor',
}) => {
  const { settlementId } = useParams<{ settlementId: string }>();
  const navigate = useNavigate();
  const { consignorId, loading: idLoading } = useConsignorId(consignorIdOverride);

  const [settlement, setSettlement] = useState<(SettlementReport & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!consignorId || !settlementId) return;
    loadSettlement(consignorId, settlementId);
  }, [consignorId, settlementId]);

  const loadSettlement = async (cid: string, sid: string) => {
    try {
      const snap = await getDoc(doc(db, 'consignors', cid, 'settlements', sid));
      if (!snap.exists()) {
        toast.error('Settlement not found.');
        navigate(`${basePath}/dashboard`);
        return;
      }

      const data = snap.data() as SettlementReport;

      // Security: settlement is under /consignors/{cid}/settlements — Firestore path is
      // already scoped to this consignorId, but verify the field too for defense-in-depth
      if (data.consignorId && data.consignorId !== cid) {
        toast.error('You do not have access to this settlement.');
        navigate(`${basePath}/dashboard`);
        return;
      }

      setSettlement({
        id: snap.id,
        ...data,
        generatedAt: (data.generatedAt as any)?.toDate?.() ?? data.generatedAt,
        paymentDueDate: (data.paymentDueDate as any)?.toDate?.() ?? data.paymentDueDate,
      });
    } catch (err) {
      console.error('Error loading settlement:', err);
      toast.error('Failed to load settlement.');
    } finally {
      setLoading(false);
    }
  };

  if (idLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto card py-20 text-center text-gray-500">
        <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p>Loading settlement…</p>
      </div>
    );
  }

  if (!settlement) return null;

  const { totals, lineItems = [], unsoldItems = [], paymentInfo } = settlement;
  const isPaid = !!paymentInfo?.paidAt;
  const paymentDue = settlement.paymentDueDate as unknown as Date;
  const generatedAt = settlement.generatedAt as unknown as Date;

  // Derived: marketing + labor fees = totals.totalFees
  const commissionAmt  = totals.totalCommission;
  const feesAmt        = totals.totalFees;
  const grossAmt       = totals.grossSales;
  const netAmt         = totals.netPayment;

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to={`${basePath}/dashboard`} className="hover:text-primary-600 transition-colors">My Portal</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Settlement {settlement.id}</span>
      </nav>

      {/* Header + print */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Settlement Statement
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {generatedAt ? `Generated ${format(generatedAt, 'MMMM d, yyyy')}` : ''}
            {paymentDue
              ? ` · Payment due ${format(paymentDue, 'MMMM d, yyyy')}`
              : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={isPaid ? 'paid' : 'unpaid'} />
          <button
            onClick={() => window.print()}
            className="btn-secondary flex items-center gap-2 print:hidden"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
      </div>

      {/* ── Net Payout Hero ──────────────────────────────────────────────── */}
      <div className="rounded-xl bg-green-50 border-2 border-green-300 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-1">
            Net Payout to You
          </p>
          <p className="text-5xl font-bold text-green-700">${netAmt.toFixed(2)}</p>
        </div>
        <div className="text-sm text-green-800 space-y-1">
          <p>{totals.itemsSold} item{totals.itemsSold !== 1 ? 's' : ''} sold</p>
          {paymentInfo?.method && (
            <p className="flex items-center gap-2">
              Via <StatusBadge status={paymentInfo.method} />
            </p>
          )}
          {isPaid && paymentInfo?.paidAt && (
            <p>Paid {format((paymentInfo.paidAt as any)?.toDate?.() ?? paymentInfo.paidAt, 'MMM d, yyyy')}</p>
          )}
          {!isPaid && paymentDue && (
            <p className="font-semibold">Due {format(paymentDue, 'MMM d, yyyy')}</p>
          )}
        </div>
      </div>

      {/* ── Sold Items ───────────────────────────────────────────────────── */}
      {lineItems.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Sold Items ({lineItems.length})
          </h2>
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Lot #', 'Description', 'Final Price', `Commission`, 'Fees', 'Net to You'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {lineItems.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-mono text-gray-700 whitespace-nowrap">
                      {item.lotNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                      <p className="line-clamp-2">{item.description}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {fmt(item.soldPrice)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 whitespace-nowrap">
                      −{fmt(item.commissionAmount)}
                      <span className="text-gray-400 text-xs ml-1">({item.commissionRate}%)</span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {item.fees > 0
                        ? <span className="text-red-600">−{fmt(item.fees)}</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-green-600 whitespace-nowrap">
                      {fmt(item.netAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                    Totals
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900">{fmt(grossAmt)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-red-600">−{fmt(commissionAmt)}</td>
                  <td className="px-4 py-3 text-sm font-bold text-red-600">
                    {feesAmt > 0 ? `−${fmt(feesAmt)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">{fmt(netAmt)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Unsold Items ─────────────────────────────────────────────────── */}
      {unsoldItems.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Unsold Items ({unsoldItems.length})
          </h2>
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['CO Item ID', 'Description', 'Disposition', 'Fee'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {unsoldItems.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.coItemId}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`capitalize ${
                        item.action === 'return'  ? 'text-blue-600'  :
                        item.action === 'rerun'   ? 'text-yellow-700' :
                        'text-gray-500'
                      }`}>
                        {item.action ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {item.feeApplied
                        ? <span className="text-red-600">−{fmt(item.feeApplied)}</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Financial Breakdown Summary ──────────────────────────────────── */}
      <div className="card max-w-md ml-auto">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Financial Breakdown</h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-baseline">
            <span className="font-semibold text-gray-700">Gross Revenue</span>
            <span className="font-bold text-gray-900 text-base">{fmt(grossAmt)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-gray-600">Commission</span>
            <span className="text-red-600 font-medium">−{fmt(commissionAmt)}</span>
          </div>
          {feesAmt > 0 && (
            <div className="flex justify-between items-baseline">
              <span className="text-gray-600">Marketing &amp; Labor Fees</span>
              <span className="text-red-600 font-medium">−{fmt(feesAmt)}</span>
            </div>
          )}

          {/* Net payout row */}
          <div className="flex justify-between items-center pt-4 mt-2 border-t-2 border-gray-200">
            <span className="font-bold text-gray-900 text-base">Net Payout</span>
            <span className="font-bold text-green-600 text-2xl">{fmt(netAmt)}</span>
          </div>
        </div>
      </div>

      {/* ── Payment Info ─────────────────────────────────────────────────── */}
      <div className="card">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Payment Information</h2>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500 mb-0.5">Status</p>
            <StatusBadge status={isPaid ? 'paid' : 'unpaid'} />
          </div>
          {paymentInfo?.method && (
            <div>
              <p className="text-gray-500 mb-0.5">Payment Method</p>
              <StatusBadge status={paymentInfo.method} />
            </div>
          )}
          {paymentDue && (
            <div>
              <p className="text-gray-500 mb-0.5">Due Date</p>
              <p className={`font-semibold ${!isPaid && new Date() > paymentDue ? 'text-red-600' : 'text-gray-900'}`}>
                {format(paymentDue, 'MMMM d, yyyy')}
              </p>
            </div>
          )}
          {paymentInfo?.paidAt && (
            <div>
              <p className="text-gray-500 mb-0.5">Paid On</p>
              <p className="font-semibold text-green-700">
                {format((paymentInfo.paidAt as any)?.toDate?.() ?? paymentInfo.paidAt, 'MMMM d, yyyy')}
              </p>
            </div>
          )}
          {paymentInfo?.checkNumber && (
            <div>
              <p className="text-gray-500 mb-0.5">Check Number</p>
              <p className="font-mono text-gray-900">{paymentInfo.checkNumber}</p>
            </div>
          )}
          {paymentInfo?.transactionId && (
            <div>
              <p className="text-gray-500 mb-0.5">Transaction ID</p>
              <p className="font-mono text-gray-900 text-xs">{paymentInfo.transactionId}</p>
            </div>
          )}
        </div>

        {!isPaid && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Payment is issued by check within 21 days of the conclusion of the sale.
            Contact Schmidt Auctions if you have questions about your payment timeline.
          </div>
        )}
      </div>

      {/* Back */}
      <div className="flex gap-3 print:hidden">
        <Link to={`${basePath}/dashboard`} className="btn-secondary">← Back to Portal</Link>
      </div>
    </div>
  );
};

export default ConsignorSettlementView;
